import { getScreenInfo } from '../lib/storage'

/**
 * Tiny chip that identifies *which* physical TV this is. Shows the screen
 * name (e.g. "Main Studio") and, when available, the room name. Rendered in
 * the corner of every main screen so coaches and admins can instantly tell
 * TVs apart — vital when a gym runs several side-by-side.
 *
 * Pure display: reads from localStorage on render. If no info is stored it
 * renders nothing so older pairings don't show a broken chip.
 *
 * Positioning is left to the caller via `className` / wrapper — this keeps
 * the component CSS-agnostic (top-right on dark screens, muted on ambient).
 */
interface Props {
  /** Optional Tailwind overrides, e.g. "text-white/20 text-[10px]". */
  className?: string
  /** When true, draws a subtle pill around the text. Off for ultra-ambient. */
  variant?: 'plain' | 'pill'
}

export default function ScreenBadge({ className = '', variant = 'pill' }: Props) {
  const info = getScreenInfo()
  if (!info) return null

  const label = info.roomName
    ? `${info.screenName} · ${info.roomName}`
    : info.screenName

  if (variant === 'plain') {
    return (
      <span className={`font-semibold tracking-widest uppercase ${className}`}>
        {label}
      </span>
    )
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 ${className}`}
      style={{
        background: 'rgba(241, 238, 232, 0.04)',
        border: '1px solid rgba(241, 238, 232, 0.08)',
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: '#30D158', boxShadow: '0 0 6px #30D15880' }}
      />
      <span className="font-bold tracking-[0.18em] uppercase">{label}</span>
    </div>
  )
}
